// Weekly Management Summary list (cadence="week") — built/refreshed straight
// from each project's WSR, one per project per reporting week. Legacy
// Fortnightly Summary records (cadence="fortnight", FN- numbering, 2-week
// rollups) are listed read-only below. Visible to internal staff + Client
// Admin only (toggled from WeeklyReportsPage.jsx). Follows the week selector
// + client/site/project filters at the top of the page.
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileClock, ArrowRight, Download, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtDate } from "./wsrConstants";

const STATUS_META = {
    draft: { label: "Draft", bg: "#FDF0DC", fg: "#B5720A" },
    released: { label: "Released", bg: "#EAF1FB", fg: "#2A5DB0" },
    final: { label: "Final", bg: "#E4F1E6", fg: "#2E7D32" },
    signed: { label: "Signed", bg: "#EAF1FB", fg: "#2A5DB0" },
    sent: { label: "Sent", bg: "#E4F1E6", fg: "#2E7D32" },
};

export default function FortnightlyReportsPanel({ clientId, projectId, siteId, weekStart }) {
    const navigate = useNavigate();
    const [rows, setRows] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(new Set());
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        setLoading(true);
        setSelected(new Set());
        const params = {};
        if (clientId) params.client_id = clientId;
        if (projectId) params.project_id = projectId;
        if (siteId) params.site_id = siteId;
        if (weekStart) params.week_start = weekStart;
        api.get("/fortnightly-reports", { params })
            .then((r) => setRows(r.data))
            .catch((e) => toast.error(formatApiError(e?.response?.data?.detail)))
            .finally(() => setLoading(false));
    }, [clientId, projectId, siteId, weekStart]);

    const weeklyRows = useMemo(() => (rows || []).filter((r) => r.cadence === "week"), [rows]);
    const legacyRows = useMemo(() => (rows || []).filter((r) => r.cadence !== "week"), [rows]);

    const weekGroups = useMemo(() => {
        const byWeek = new Map();
        weeklyRows.forEach((r) => {
            if (!byWeek.has(r.week_start)) byWeek.set(r.week_start, []);
            byWeek.get(r.week_start).push(r);
        });
        return Array.from(byWeek.entries())
            .sort((a, b) => (a[0] < b[0] ? 1 : -1)) // newest week first
            .map(([ws, items]) => [ws, items.sort((a, b) => (a.project_name || "").localeCompare(b.project_name || ""))]);
    }, [weeklyRows]);

    if (loading) return <div className="font-serif-italic text-[#9A968F] py-10 text-center">Loading…</div>;

    const allShownIds = weeklyRows.map((r) => r.id);
    const allSelected = allShownIds.length > 0 && allShownIds.every((id) => selected.has(id));
    const selectedClientIds = new Set(weeklyRows.filter((r) => selected.has(r.id)).map((r) => r.client_id));

    const toggleOne = (r) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(r.id)) { next.delete(r.id); return next; }
            if (next.size > 0 && !selectedClientIds.has(r.client_id)) return new Set([r.id]); // pack is same-client only
            next.add(r.id);
            return next;
        });
    };

    const toggleAll = () => {
        if (allSelected) { setSelected(new Set()); return; }
        // "Select all" only makes sense within one client's summaries at a time.
        const firstClient = weeklyRows[0]?.client_id;
        const sameClient = weeklyRows.filter((r) => r.client_id === firstClient);
        setSelected(new Set(sameClient.map((r) => r.id)));
    };

    const downloadPack = async () => {
        if (selected.size === 0) return;
        setDownloading(true);
        try {
            const params = new URLSearchParams();
            Array.from(selected).forEach((id) => params.append("report_ids", id));
            const res = await api.get(`/fortnightly-reports/meeting-pack?${params.toString()}`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = "MS-meeting-pack.pdf";
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't build the meeting pack");
        } finally {
            setDownloading(false);
        }
    };

    return (
        <div data-testid="fortnightly-reports-panel" className="bg-white rounded-2xl border border-[#E5E0DA] shadow-brand-sm overflow-hidden">
            <div className="px-5 py-3.5 border-b border-[#F0ECE5] flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <div className="font-bold text-[14px] text-[#1F1F1F]">Weekly Management Summary</div>
                    <p className="text-[12px] text-[#8B867E] mt-0.5">Built from each week's Weekly Site Report — one per project. Select any summaries (same client) for a meeting pack.</p>
                </div>
                <button data-testid="ms-download-pack-btn" disabled={selected.size === 0 || downloading} onClick={downloadPack}
                        className="flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[12.5px] font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        style={{ background: "#C75A1B", color: "#fff" }}>
                    {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                    {selected.size > 0 ? `${selected.size} selected – Download pack` : "Download pack"}
                </button>
            </div>
            {weeklyRows.length === 0 ? (
                <div data-testid="fortnightly-reports-empty" className="p-8 text-center text-[#9A968F]">
                    <FileClock size={28} className="mx-auto mb-2 opacity-60" />
                    No Weekly Management Summaries for this selection yet. They're built automatically once a project's Weekly Site Report is submitted.
                </div>
            ) : (
                <>
                    <div className="px-5 py-2 bg-[#FAF8F5] border-b border-[#F0ECE5] flex items-center gap-2.5">
                        <input type="checkbox" data-testid="ms-select-all" checked={allSelected} onChange={toggleAll} />
                        <label className="text-[12px] font-semibold text-[#6F6A64] cursor-pointer" onClick={toggleAll}>Select all ({weeklyRows.length} shown)</label>
                    </div>
                    {weekGroups.map(([ws, items]) => (
                        <div key={ws}>
                            <div data-testid={`ms-week-heading-${ws}`} className="px-5 py-2 bg-[#F5F3F0] text-[11px] font-bold uppercase tracking-[.05em] text-[#8B867E]">
                                Week commencing {fmtDate(ws)}
                            </div>
                            <div className="divide-y divide-[#F0ECE5]">
                                {items.map((r) => {
                                    const meta = STATUS_META[r.status] || STATUS_META.draft;
                                    const checked = selected.has(r.id);
                                    return (
                                        <div key={r.id} data-testid={`fortnightly-report-row-${r.id}`}
                                             className="w-full text-left px-5 py-3.5 flex items-center justify-between gap-3 flex-wrap hover:bg-[#F5F3F0] transition-colors">
                                            <label className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer" onClick={(e) => e.stopPropagation()}>
                                                <input type="checkbox" data-testid={`ms-select-${r.id}`} checked={checked} onChange={() => toggleOne(r)} />
                                                <button onClick={() => navigate(`/app/weekly-reports/management-summary/${r.id}`)} className="text-left">
                                                    <div className="font-semibold text-[13.5px] text-[#1F1F1F]">{r.project_name} <span className="text-[#9A968F] font-normal">· {r.client_name}</span></div>
                                                    <div className="text-[12px] text-[#6F6A64] mt-0.5">{r.report_number} · Week {fmtDate(r.week_start)} – {fmtDate(r.week_end)}</div>
                                                </button>
                                            </label>
                                            <div className="flex items-center gap-2.5">
                                                {r.late && <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold" style={{ background: "#FBE6E6", color: "#B0262B" }}>Late</span>}
                                                <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold" style={{ background: meta.bg, color: meta.fg }}>{meta.label}</span>
                                                <ArrowRight size={15} className="text-[#C75A1B]" onClick={() => navigate(`/app/weekly-reports/management-summary/${r.id}`)} />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </>
            )}
            {legacyRows.length > 0 && (
                <>
                    <div className="px-5 py-2.5 border-t border-b border-[#F0ECE5] bg-[#FAF8F5]">
                        <div className="font-semibold text-[12.5px] text-[#6F6A64]">Legacy Fortnightly Summaries (read-only, 2-week rollups)</div>
                    </div>
                    <div className="divide-y divide-[#F0ECE5]">
                        {legacyRows.map((r) => {
                            const meta = STATUS_META[r.status] || STATUS_META.draft;
                            return (
                                <button key={r.id} data-testid={`fortnightly-report-row-${r.id}`}
                                        onClick={() => navigate(`/app/weekly-reports/fortnightly/${r.id}`)}
                                        className="w-full text-left px-5 py-3.5 flex items-center justify-between gap-3 flex-wrap hover:bg-[#F5F3F0] transition-colors">
                                    <div>
                                        <div className="font-semibold text-[13.5px] text-[#1F1F1F]">{r.project_name} <span className="text-[#9A968F] font-normal">· {r.client_name}</span></div>
                                        <div className="text-[12px] text-[#6F6A64] mt-0.5">{r.report_number} · Fortnight {fmtDate(r.fortnight_start)} – {fmtDate(r.fortnight_end)}</div>
                                    </div>
                                    <div className="flex items-center gap-2.5">
                                        <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold" style={{ background: meta.bg, color: meta.fg }}>{meta.label}</span>
                                        <ArrowRight size={15} className="text-[#C75A1B]" />
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
}
